import type { UpdateMutationResult } from "@/customTypings/api/UpdateMutation";
import type {
    TechnicalCompetenceSchemeCreatePayload,
    TechnicalCompetenceSchemeUpdatePayload,
    ITechnicalCompetenceSchemeDetail,
} from "@/customTypings/reference/TechnicalCompetenceScheme";
import { TECHNICAL_COMPETENCE_SCHEME_CREATE_CONFLICT } from "@/customTypings/reference/TechnicalCompetenceScheme";
import type { ReferenceRestoreDeletedPayload } from "@/utils/reference/referenceCreateConflict";
import { authHeader, handleResponse } from "../../utils";
import { appendIncludeAuditQuery, type DetailGetByIdOptions } from "@/utils/api/detailGetOptions";
import { postReferenceCreate } from "@/utils/reference/referenceCreateConflict";

export const technicalCompetenceSchemesService = {
    getById,
    create,
    restoreDeletedWithCreateValues,
    update,
    disable,
    enable,
    softDelete,
    restore,
};

function getById(id: string, options?: DetailGetByIdOptions) {
    const requestOptions: RequestInit = {
        method: "GET",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        appendIncludeAuditQuery(
            `/api/referencedata/technical-competence-schemes/${encodeURIComponent(id)}`,
            options
        ),
        requestOptions
    ).then(handleResponse) as Promise<ITechnicalCompetenceSchemeDetail>;
}

function create(payload: TechnicalCompetenceSchemeCreatePayload) {
    return postReferenceCreate<ITechnicalCompetenceSchemeDetail>(
        `/api/referencedata/technical-competence-schemes/create`,
        payload,
        TECHNICAL_COMPETENCE_SCHEME_CREATE_CONFLICT.deletedRestoreAvailable
    );
}

function restoreDeletedWithCreateValues(payload: ReferenceRestoreDeletedPayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(
        `/api/referencedata/technical-competence-schemes/restore-deleted-with-create-values`,
        requestOptions
    ).then(handleResponse) as Promise<ITechnicalCompetenceSchemeDetail>;
}

function update(payload: TechnicalCompetenceSchemeUpdatePayload) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
        body: JSON.stringify(payload),
    };
    return fetch(`/api/referencedata/technical-competence-schemes/update`, requestOptions).then(
        handleResponse
    ) as Promise<UpdateMutationResult<ITechnicalCompetenceSchemeDetail>>;
}

function disable(id: string) {
    return postQuery("disable", id);
}

function enable(id: string) {
    return postQuery("enable", id);
}

function softDelete(id: string) {
    return postQuery("softdelete", id);
}

function restore(id: string) {
    return postQuery("restore", id);
}

function postQuery(action: string, id: string) {
    const requestOptions: RequestInit = {
        method: "POST",
        headers: authHeader(),
        credentials: "include",
    };
    return fetch(
        `/api/referencedata/technical-competence-schemes/${action}?id=${encodeURIComponent(id)}`,
        requestOptions
    ).then(handleResponse);
}
